import type {
  ContentObject,
  CustomLink,
  CustomLinksObject,
} from '../../lib/flotiq-client/types/api-response';

import { useMemo } from 'react';

import { getTestProps, interpolateTemplate } from '../../lib/helpers';

import Panel from '../../components/Panel/Panel';

import { useContentObject } from '../../hooks/api';

import LinkButton from '../LinkButton/LinkButton';

const CUSTOM_LINKS_CTD = '_custom_links';
const CO_ID = 'flotiq.custom-links';

interface CustomLinksPanelProps {
  /**
   * Name of the content type definition of object the form is editing
   */
  contentTypeName?: string;
  /**
   * The content object currently being edited
   */
  contentObject?: ContentObject | null;
  /**
   * Whether a new object is being created
   */
  create?: boolean;
  /**
   * Whether the object is being duplicated
   */
  duplicate?: boolean;
  /**
   * Whether the panel is hidden
   */
  hidden?: boolean;
  /**
   * Test id for the panel
   */
  testId?: string;
}

const CustomLinksPanel = ({
  contentTypeName,
  contentObject,
  create,
  duplicate,
  hidden,
  testId,
}: CustomLinksPanelProps) => {
  const { entity: settingsObject } = useContentObject(CUSTOM_LINKS_CTD, CO_ID);
  const settings = settingsObject as CustomLinksObject | undefined;

  const isRenderableCustomLink = (
    entry: CustomLink,
    contentTypeName: string,
  ) => {
    if (!entry.url_template || !entry.link_template) return false;
    return (
      !entry.content_types.length ||
      entry.content_types.some((ctd) => ctd === contentTypeName)
    );
  };

  const entries = useMemo<CustomLink[]>(() => {
    if (!settings?.settings?.length || !contentTypeName) return [];
    return settings.settings.filter((entry) =>
      isRenderableCustomLink(entry, contentTypeName),
    );
  }, [settings, contentTypeName]);

  if (!contentObject || !contentTypeName || create || duplicate) return null;
  if (!entries.length) return null;

  return (
    <Panel
      open
      isCollapsable={false}
      additionalContainerClasses={hidden ? 'xl:hidden' : ''}
      additionalChildrenContainerClasses="flex flex-col gap-2 p-0"
      testId={testId}
      id="content_object-custom-links"
    >
      <div className="flex justify-center gap-4 flex-wrap">
        {entries.map((entry, idx) => {
          const url = interpolateTemplate(entry.url_template, contentObject);
          const displayName = interpolateTemplate(
            entry.link_template,
            contentObject,
          );
          return (
            <LinkButton
              key={`${entry.url_template}-${idx}`}
              link={url}
              target="_blank"
              rel="noreferrer"
              buttonSize="md"
              buttonColor="blueBordered"
              additionalClasses="font-medium max-w-full text-black"
              {...getTestProps(testId, 'custom-link', 'testId')}
            >
              <span>{displayName}</span>
            </LinkButton>
          );
        })}
      </div>
    </Panel>
  );
};

export default CustomLinksPanel;
